import type { SkuRegionPrice } from "../schemas/enums/crossBorder.enum";

/** Whole-percent multiplier — used to shift a money value two decimal places
 *  before rounding to the nearest integer cent. */
const PERCENT = 100;

/** How many floating-point ULPs of tolerance to apply when nudging a scaled
 *  value before rounding — enough to absorb the ~1-ULP error a single
 *  multiplication introduces, without being anywhere near the half-cent
 *  boundary itself (see `roundMoney`). */
const ROUNDING_TOLERANCE_ULPS = 4;

/** Rounds a money value to 2 decimal places.
 *
 *  A fixed epsilon can't do this correctly: IEEE-754's absolute precision
 *  shrinks or grows with the magnitude of the number, so a nudge sized for
 *  small values is invisible at large magnitudes (e.g. `262144.035 * 100`
 *  evaluates to `26214403.499999996`, off by ~4e-9 — bigger than a fixed
 *  `1e-9` nudge) while a nudge sized for large values would incorrectly
 *  round up genuinely sub-boundary small values. */
export const roundMoney = (value: number): number => {
  const scaled = value * PERCENT;
  const tolerance = Math.abs(scaled) * Number.EPSILON * ROUNDING_TOLERANCE_ULPS;
  return Math.round(scaled + tolerance) / PERCENT;
};

/** Rounds every money field (`msrp`, `wholesalePrice`, `roasterWholesalePrice`)
 *  on each entry to 2 decimal places. Returns a new array — the input entries
 *  are not mutated. Passes `undefined` through unchanged. */
export const roundSkuPrices = (skuPrices: SkuRegionPrice[] | undefined): SkuRegionPrice[] | undefined =>
  skuPrices?.map((entry) => ({
    ...entry,
    msrp: roundMoney(entry.msrp),
    wholesalePrice: roundMoney(entry.wholesalePrice),
    ...(entry.roasterWholesalePrice !== undefined ? { roasterWholesalePrice: roundMoney(entry.roasterWholesalePrice) } : {}),
  }));
